import type { GraphScene } from './types'

/**
 * Two scenes over one model. Both are grounded in Thinai's own docs — the five
 * domains come from docs-public/overview.md, the agent roster from
 * docs-public/features/ai-agents.md.
 *
 * Desktop coordinates assume a wide canvas (~1100×640, so roughly 1.7:1).
 * Mobile coordinates kick in below 640px where a five-across row cannot fit;
 * there the flow becomes a two-column zigzag.
 */

/* ── Scene A: the five landscapes ─────────────────────────────────────────────
   A change entering the platform, passing through all five domains, and
   shipping. Mirrors the real worked example in overview.md (the auth
   migration story, which walks Kurinji → Mullai → Marutam → Neytal → Pālai). */

export const LANDSCAPES_SCENE: GraphScene = {
  nodes: [
    { id: 'work', role: 'source', tag: 'input', name: 'Change', x: 12, y: 10, xMobile: 50, yMobile: 7 },

    // Rendered as one composite card ("the engine") rather than two nodes.
    { id: 'core', role: 'engine', tag: 'rust', name: 'thinai-core', x: 46, y: 10, xMobile: 50, yMobile: 21 },
    { id: 'knuckles', role: 'graph', tag: 'store', name: 'Knuckles', x: 64, y: 10, xMobile: 50, yMobile: 21 },

    { id: 'kurinji', role: 'source', tag: 'product', name: 'Kurinji', x: 12, y: 52, xMobile: 27, yMobile: 38 },
    { id: 'mullai', role: 'agent', tag: 'knowledge', name: 'Mullai', x: 31, y: 52, xMobile: 73, yMobile: 38 },
    { id: 'marutam', role: 'engine', tag: 'code', name: 'Marutam', x: 50, y: 52, xMobile: 27, yMobile: 55 },
    { id: 'neytal', role: 'graph', tag: 'quality', name: 'Neytal', x: 69, y: 52, xMobile: 73, yMobile: 55 },
    { id: 'palai', role: 'output', tag: 'delivery', name: 'Pālai', x: 88, y: 52, xMobile: 27, yMobile: 72 },

    { id: 'prod', role: 'output', tag: 'result', name: 'Production', x: 50, y: 90, xMobile: 73, yMobile: 72 },
  ],

  edges: [
    { from: 'work', to: 'core', activeOnly: true },
    { from: 'core', to: 'knuckles', internal: true },
    { from: 'knuckles', to: 'kurinji' },
    { from: 'kurinji', to: 'mullai' },
    { from: 'mullai', to: 'marutam' },
    { from: 'marutam', to: 'neytal' },
    { from: 'neytal', to: 'palai' },
    { from: 'palai', to: 'prod', solid: true },
  ],

  groups: [
    {
      ids: ['core', 'knuckles'],
      tag: 'platform',
      primary: true,
      items: [
        { id: 'core', name: 'thinai-core', role: 'graph engine', color: '#B98CF0' },
        { id: 'knuckles', name: 'Knuckles', role: 'hybrid store', color: '#E8A33D' },
      ],
    },
  ],

  schedule: [
    { show: ['work', 'core', 'knuckles'], focus: 'core', ms: 1000 },
    { show: ['kurinji'], focus: 'kurinji', ms: 900 },
    { show: ['mullai'], focus: 'mullai', ms: 900 },
    { show: ['marutam'], focus: 'marutam', ms: 900 },
    { show: ['neytal'], focus: 'neytal', ms: 950 },
    { show: ['palai'], focus: 'palai', ms: 950 },
    { show: ['prod'], focus: 'prod', ms: 1300 },
  ],

  startOffsetMs: 600,
}

/* ── Scene B: the knowledge graph ─────────────────────────────────────────────
   Fragmented sources resolving into one graph, then fanning out to the agents
   that act on it. A hub, deliberately a different shape from scene A's march. */

export const GRAPH_SCENE: GraphScene = {
  nodes: [
    { id: 'repos', role: 'source', tag: 'source', name: 'Repositories', x: 13, y: 20, xMobile: 50, yMobile: 7 },
    { id: 'issues', role: 'source', tag: 'source', name: 'Issues', x: 13, y: 50, xMobile: 50, yMobile: 21 },
    { id: 'decisions', role: 'source', tag: 'source', name: 'Decisions', x: 13, y: 80, xMobile: 50, yMobile: 35 },

    { id: 'kg', role: 'engine', tag: 'engine', name: 'Knowledge graph', x: 48, y: 50, xMobile: 50, yMobile: 53, primary: true },

    { id: 'review', role: 'agent', tag: 'agent', name: 'Code Review', x: 83, y: 20, xMobile: 27, yMobile: 73 },
    { id: 'testgen', role: 'agent', tag: 'agent', name: 'Test Generation', x: 83, y: 50, xMobile: 73, yMobile: 73 },
    { id: 'security', role: 'agent', tag: 'agent', name: 'Security Scan', x: 83, y: 80, xMobile: 50, yMobile: 89 },
  ],

  edges: [
    { from: 'repos', to: 'kg' },
    { from: 'issues', to: 'kg' },
    { from: 'decisions', to: 'kg' },
    { from: 'kg', to: 'review' },
    { from: 'kg', to: 'testgen' },
    { from: 'kg', to: 'security' },
  ],

  schedule: [
    { show: ['repos', 'issues', 'decisions'], focus: 'repos', ms: 1000 },
    { show: ['kg'], focus: 'kg', ms: 1400 },
    { show: ['review'], focus: 'review', ms: 900 },
    { show: ['testgen'], focus: 'testgen', ms: 900 },
    { show: ['security'], focus: 'security', ms: 900 },
  ],

  startOffsetMs: 300,
}

export const SCENES = {
  landscapes: { id: 'landscapes', scene: LANDSCAPES_SCENE },
  graph: { id: 'graph', scene: GRAPH_SCENE },
} as const

export type SceneId = keyof typeof SCENES
